import { useState, useEffect } from 'react';

type FeatureObject = {
  feature?: string;
  name?: string;
  text?: string;
  description?: string;
  title?: string;
  value?: string;
  label?: string;
  display?: string;
  content?: string;
};

type PlanWithExtensions = {
  pricesArray?: unknown[];
  relationships?: {
    prices?: {
      data?: unknown[];
    };
  };
  translations?: {
    displayName?: Record<string, unknown>;
    display_name?: Record<string, unknown>;
    name?: Record<string, unknown>;
    description?: Record<string, unknown>;
  };
};
import { Loader2, Plus, X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
// Translation components ready for use when needed
// import { TranslationInput } from '@/components/billing/translations/TranslationInput';
// import { TranslationFeaturesList } from '@/components/billing/translations/TranslationFeaturesList';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  OneTimePaymentPlan, 
  MultiCurrencyPrice
} from '@/types/billing';
import { 
  useCreateOneTimePlan, 
  useUpdateOneTimePlan,
  useOneTimePlanCalculation
} from '@/hooks/billing/useOneTimePlans';
import { 
  OneTimePlanCreateData
} from '@/services/billing/one-time-plans.service';
import { TranslationButton, TranslationDialog } from '@/components/dialogs/TranslationDialog';
import { FeaturesTranslationDialog } from '@/components/dialogs/FeaturesTranslationDialog';
import { useTranslation } from 'react-i18next';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

interface OneTimePlanEditorProps {
  productId: string;
  plan?: Partial<OneTimePaymentPlan>;
  isOpen: boolean;
  onClose: () => void;
  activeCurrencies?: string[];
  defaultCurrency?: string;
  supportedLanguages?: string[];
  planType?: 'individual' | 'teams';
  currentLanguage?: string;
}

export function OneTimePlanEditor({
  productId,
  plan,
  isOpen,
  onClose,
  activeCurrencies = ['USD'],
  defaultCurrency = 'USD',
  supportedLanguages = ['en'],
  planType = 'individual',
  currentLanguage = 'en' // Ready for translation implementation
}: OneTimePlanEditorProps) {
  const { t } = useTranslation();
  const isEditing = !!plan?.id;
  
  // Debug: Log the supported languages
  console.log('OneTimePlanEditor - supportedLanguages:', supportedLanguages);
  console.log('OneTimePlanEditor - supportedLanguages length:', supportedLanguages?.length);
  
  // Translation params ready for use
  void currentLanguage;
  
  console.log('OneTimePlanEditor - Props:', {
    productId,
    plan,
    isOpen,
    activeCurrencies,
    defaultCurrency,
    planType
  });
  
  // Form state
  const [formData, setFormData] = useState<OneTimePlanCreateData>({
    name: '',
    description: '',
    type: planType,
    prices: { [defaultCurrency]: 0 },
    defaultCurrency,
    accessDurationMonths: 12,
    status: 'draft',
    includedSeats: planType === 'teams' ? 5 : undefined,
    pricesPerAdditionalSeat: planType === 'teams' ? { [defaultCurrency]: 0 } : undefined
  });

  // Additional UI state
  const [features, setFeatures] = useState<string[]>([]);
  const [newFeature, setNewFeature] = useState('');
  const [showTranslationDialog, setShowTranslationDialog] = useState(false);
  const [showDescriptionTranslationDialog, setShowDescriptionTranslationDialog] = useState(false);
  const [showFeaturesTranslationDialog, setShowFeaturesTranslationDialog] = useState(false);
  const [nameTranslations, setNameTranslations] = useState<{ [lang: string]: string }>({});
  const [descriptionTranslations, setDescriptionTranslations] = useState<{ [lang: string]: string }>({});
  const [featuresTranslations, setFeaturesTranslations] = useState<{ [lang: string]: string[] }>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [customDuration, setCustomDuration] = useState('12');
  const [isRecommended, setIsRecommended] = useState(false);
  const [offerFreeTrial, setOfferFreeTrial] = useState(false);
  const [freeTrialDays, setFreeTrialDays] = useState(14);
  const [selectedFeatureFlags, setSelectedFeatureFlags] = useState<{ [key: string]: boolean }>({});
  
  // Store price IDs for updating existing prices
  const [priceIds, setPriceIds] = useState<{ [currency: string]: string }>({});
  
  // Track original prices to detect changes
  const [originalPrices, setOriginalPrices] = useState<{ [key: string]: number }>({});

  // Calculation helpers
  const { validateAccessDuration } = useOneTimePlanCalculation();
  
  // Fetch available feature flags
  const { data: featureFlagsResponse } = useFeatureFlags(productId);
  const availableFeatureFlags = featureFlagsResponse?.data || [];
  
  // Mutations
  const createMutation = useCreateOneTimePlan();
  const updateMutation = useUpdateOneTimePlan();

  // Initialize form data from plan prop
  useEffect(() => {
    console.log('OneTimePlanEditor - useEffect triggered. Plan ID:', plan?.id, 'planType:', planType);
    
    // If we have a plan with an ID, we're editing - don't let planType changes affect this
    if (plan && plan.id) {
      console.log('OneTimePlanEditor received plan:', plan);
      console.log('accessDurationMonths:', plan.accessDurationMonths);
      console.log('isLifetime:', plan.isLifetime);
      // Handle features first - they might be strings or objects
      const planFeatures = plan.features || [];
      console.log('OneTimePlanEditor - Raw features from plan:', planFeatures);
      
      // Check if features are already in the correct format (array of strings)
      let normalizedFeatures: string[] = [];
      
      if (Array.isArray(planFeatures)) {
        normalizedFeatures = planFeatures.map(feature => {
          console.log('Processing feature:', feature, 'Type:', typeof feature);
          
          if (typeof feature === 'string') {
            // If it's already a string, just return it
            return feature;
          } else if (feature && typeof feature === 'object') {
            // Log the feature object to see its structure
            console.log('Feature object properties:', Object.keys(feature));
            console.log('Feature object values:', feature);
            
            // Try to extract text from various possible properties
            // Looking at the screenshot, the feature might have different property names
            const featureObj = feature as FeatureObject;
            const featureText = featureObj.feature || // Most likely based on screenshot
                               featureObj.name ||
                               featureObj.text ||
                               featureObj.description ||
                               featureObj.title ||
                               featureObj.value ||
                               featureObj.content ||
                               featureObj.label ||
                               '';
            
            // If still no text found, try to get the first string value from the object
            if (!featureText && typeof feature === 'object') {
              const values = Object.values(feature);
              const firstStringValue = values.find(v => typeof v === 'string' && v.trim() !== '');
              if (firstStringValue) {
                console.log('Found string value in object:', firstStringValue);
                return firstStringValue as string;
              }
            }
            
            console.log('Extracted feature text:', featureText);
            return featureText;
          }
          return '';
        }).filter(f => f.trim() !== ''); // Filter out empty strings
      } else if (typeof planFeatures === 'string') {
        // If features came as a single string, try to parse it
        try {
          const parsed = JSON.parse(planFeatures);
          if (Array.isArray(parsed)) {
            normalizedFeatures = parsed.filter(f => typeof f === 'string' && f.trim() !== '');
          }
        } catch (_error) {
          // If it's not JSON, treat it as a single feature
          normalizedFeatures = [planFeatures];
        }
      }
      
      console.log('OneTimePlanEditor - Normalized features:', normalizedFeatures);
      
      // When editing, preserve the original plan type
      const preservedType = plan.type || planType;
      console.log('OneTimePlanEditor - Preserving plan type:', plan.type, 'fallback planType:', planType, 'final:', preservedType);
      
      // Handle price ID extraction for existing plans (similar to CreditPackageEditor)
      let prices = plan.prices || { [defaultCurrency]: 0 };
      const extractedPriceIds: { [currency: string]: string } = {};
      
      console.log('OneTimePlanEditor - Full plan object:', plan);
      console.log('OneTimePlanEditor - plan.prices:', plan.prices);
      const planWithExt = plan as unknown as PlanWithExtensions;
      console.log('OneTimePlanEditor - plan.pricesArray:', planWithExt.pricesArray);
      console.log('OneTimePlanEditor - plan.relationships:', planWithExt.relationships);

      // Check for pricesArray first (contains IDs), then relationships.prices.data, then fallback to prices (legacy format)
      const pricesData = planWithExt.pricesArray ||
                        planWithExt.relationships?.prices?.data || 
                        plan.prices;
      
      if (pricesData) {
        if (Array.isArray(pricesData)) {
          // New format: array of price objects with IDs
          console.log('OneTimePlanEditor - Processing price array with IDs:', pricesData);
          const newPrices: { [currency: string]: number } = {};
          type PriceRel = { id?: string; attributes?: { currency?: string; base_price?: number; basePrice?: number } };
          type PriceDirect = { id?: string; currency?: string; amount?: number; basePrice?: number; base_price?: number; price?: number; value?: number };
          (pricesData as Array<PriceRel | PriceDirect>).forEach((priceObj) => {
            // Handle JSON:API format where data comes from relationships
            let currency, amount, id;
            
            if ('attributes' in priceObj && priceObj.attributes) {
              // JSON:API relationship format
              currency = priceObj.attributes.currency;
              amount = priceObj.attributes.base_price || priceObj.attributes.basePrice || 0;
              id = priceObj.id;
            } else {
              // Direct object format (already deserialized)
              const p = priceObj as PriceDirect;
              currency = p.currency;
              amount = p.amount || p.basePrice || p.base_price || p.price || p.value || 0;
              id = p.id;
            }
            
            console.log(`OneTimePlanEditor - Processing price object:`, priceObj);
            console.log(`OneTimePlanEditor - Extracted amount for ${currency}:`, amount);
            
            if (currency) {
              newPrices[currency] = amount;
              if (id) {
                extractedPriceIds[currency] = id;
                console.log(`OneTimePlanEditor - Extracted price ID for ${currency}:`, id);
              }
            }
          });
          prices = newPrices;
        } else if (typeof pricesData === 'object') {
          // Old format: object with currency keys
          prices = pricesData;
        }
      }
      
      console.log('OneTimePlanEditor - Final prices:', prices);
      console.log('OneTimePlanEditor - Extracted price IDs:', extractedPriceIds);
      
      setFormData({
        name: plan.name || '',
        description: plan.description || '',
        type: preservedType,
        prices: prices,
        defaultCurrency: plan.defaultCurrency || defaultCurrency,
        accessDurationMonths: plan.accessDurationMonths || 12,
        status: plan.status || 'draft',
        includedSeats: plan.includedSeats || (preservedType === 'teams' ? 5 : undefined),
        pricesPerAdditionalSeat: plan.pricesPerAdditionalSeat || (preservedType === 'teams' ? { [defaultCurrency]: 0 } : undefined)
      });
      
      // Store price IDs for updates
      setPriceIds(extractedPriceIds);
      
      // Store original prices to track changes
      setOriginalPrices({ ...prices });
      
      setFeatures(normalizedFeatures); // Also set in separate state
      
      // Handle translations - check both new and old formats
      if (planWithExt.translations) {
        // New format with nested translations object
        const trans = planWithExt.translations;
        // Handle both camelCase (after deserialization) and snake_case
        const nameTransData = (trans?.displayName as Record<string, string>) || (trans?.display_name as Record<string, string>) || (trans?.name as Record<string, string>) || {};
        const descTransData = (trans?.description as Record<string, string>) || {};
        setNameTranslations(nameTransData as { [lang: string]: string });
        setDescriptionTranslations(descTransData as { [lang: string]: string });
        
        // Convert features array format to indexed format
        type FeatureTrans = Record<string, string>;
        const anyTrans = trans as { features?: FeatureTrans[] } | undefined;
        if (anyTrans?.features && Array.isArray(anyTrans.features)) {
          const indexedFeatures: { [lang: string]: string[] } = {};
          anyTrans.features.forEach((featureTrans, index: number) => {
            Object.keys(featureTrans).forEach(lang => {
              if (!indexedFeatures[lang]) {
                indexedFeatures[lang] = [];
              }
              indexedFeatures[lang][index] = featureTrans[lang];
            });
          });
          setFeaturesTranslations(indexedFeatures as { [lang: string]: string[] });
        } else {
          setFeaturesTranslations(plan.featuresTranslations || {});
        }
      } else {
        // Old format with flat structure
        setNameTranslations(plan.nameTranslations || {});
        setDescriptionTranslations(plan.descriptionTranslations || {});
        setFeaturesTranslations(plan.featuresTranslations || {});
      }
      const isUnlimitedValue = plan.accessDurationMonths === 'unlimited';
      console.log('Setting isUnlimited to:', isUnlimitedValue);
      setIsUnlimited(isUnlimitedValue);
      if (typeof plan.accessDurationMonths === 'number') {
        setCustomDuration(plan.accessDurationMonths.toString());
      }
      // Handle trial days - check multiple formats like the other editors
      const trialDays = plan.trialDays || plan.trial_days || plan.freeTrialDays || 0;
      
      setIsRecommended(plan.isRecommended || plan.is_popular || false);
      setOfferFreeTrial(trialDays > 0);
      setFreeTrialDays(trialDays || 14);
      setSelectedFeatureFlags(plan.featureFlags || {});
    }
    // Note: New plan initialization logic moved to separate useEffect below
  }, [plan, defaultCurrency, planType]); // Include planType for consistency

  // Separate effect for handling new plan creation (when plan is undefined/null)
  useEffect(() => {
    if (!plan || !plan.id) {
      console.log('OneTimePlanEditor - Setting up new plan with planType:', planType);
      setFormData({
        name: '',
        description: '',
        type: planType,
        prices: { [defaultCurrency]: 0 },
        defaultCurrency,
        accessDurationMonths: 12,
        status: 'draft',
        includedSeats: planType === 'teams' ? 5 : undefined,
        pricesPerAdditionalSeat: planType === 'teams' ? { [defaultCurrency]: 0 } : undefined
      });
      setFeatures([]);
      setNameTranslations({});
      setDescriptionTranslations({});
      setFeaturesTranslations({});
      setIsUnlimited(false);
      setCustomDuration('12');
      setIsRecommended(false);
      setOfferFreeTrial(false);
      setFreeTrialDays(14);
      setSelectedFeatureFlags({});
      setPriceIds({}); // Reset price IDs for new plan
      setOriginalPrices({}); // Reset original prices for new plan
    }
  }, [planType, defaultCurrency, plan]); // Include plan so effect re-evaluates if plan becomes defined

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFeatures([...features, newFeature.trim()]);
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index: number) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const handlePricesChange = (prices: MultiCurrencyPrice) => {
    setFormData({ ...formData, prices });
    
    // Clear validation errors when prices are updated
    const newErrors = { ...validationErrors };
    Object.keys(prices).forEach(currency => {
      if (prices[currency] && prices[currency] > 0) {
        delete newErrors[`price-${currency}`];
      }
    });
    setValidationErrors(newErrors);
  };


  const validatePricing = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    console.log('OneTimePlanEditor - Validating pricing for currencies:', activeCurrencies);
    console.log('OneTimePlanEditor - Form prices:', formData.prices);
    console.log('OneTimePlanEditor - Plan type:', formData.type);

    // Check main prices for all active currencies
    activeCurrencies.forEach(currency => {
      const price = formData.prices?.[currency];
      console.log(`OneTimePlanEditor - Checking ${currency}: ${price}`);
      if (!price || price <= 0) {
        errors[`price-${currency}`] = `Price required for ${currency}`;
        isValid = false;
      }
    });

    // TODO: If teams plan, also check additional seat prices when UI supports it
    // Currently disabled since the form doesn't show seat pricing fields
    // if (formData.type === 'teams') {
    //   console.log('OneTimePlanEditor - Checking team seat prices:', formData.pricesPerAdditionalSeat);
    //   activeCurrencies.forEach(currency => {
    //     const seatPrice = formData.pricesPerAdditionalSeat?.[currency];
    //     if (!seatPrice || seatPrice < 0) {
    //       errors[`seat-${currency}`] = `Seat price required for ${currency}`;
    //       isValid = false;
    //     }
    //   });
    // }

    console.log('OneTimePlanEditor - Validation errors:', errors);
    console.log('OneTimePlanEditor - Validation result:', isValid);
    setValidationErrors(errors);
    return isValid;
  };

  const handleDurationChange = (value: string) => {
    setCustomDuration(value);
    const duration = parseInt(value);
    if (!isNaN(duration) && validateAccessDuration(duration)) {
      setFormData({ ...formData, accessDurationMonths: duration });
    }
  };

  const handleUnlimitedChange = (checked: boolean) => {
    setIsUnlimited(checked);
    setFormData({ 
      ...formData, 
      accessDurationMonths: checked ? 'unlimited' : parseInt(customDuration) || 12
    });
  };


  const handleFeatureFlagToggle = (flagKey: string) => {
    setSelectedFeatureFlags(prev => ({
      ...prev,
      [flagKey]: !prev[flagKey]
    }));
  };

  const handleSave = async () => {
    // Basic form validation
    if (!formData.name.trim()) {
      toast.error(t('products.oneTimePlan.validation.nameRequired'));
      return;
    }

    // Skip pricing validation if no active currencies
    if (activeCurrencies.length > 0 && !validatePricing()) {
      toast.error(t('products.oneTimePlan.validation.pricesRequired'));
      return;
    }
    
    console.log('OneTimePlanEditor - Form Data before submission:', formData);
    console.log('OneTimePlanEditor - Features:', features);
    console.log('OneTimePlanEditor - Features Translations:', featuresTranslations);
    console.log('OneTimePlanEditor - Selected Feature Flags:', selectedFeatureFlags);
    
    // Detect which prices have actually changed (similar to CreditPackageEditor)
    const changedPrices: { [currency: string]: number } = {};
    const changedPriceIds: { [currency: string]: string } = {};
    
    if (isEditing) {
      // For existing plans, only include changed prices
      Object.keys(formData.prices).forEach(currency => {
        const currentPrice = formData.prices[currency];
        const originalPrice = originalPrices[currency];
        
        // Include price if it's new or if it has changed
        if (originalPrice === undefined || currentPrice !== originalPrice) {
          changedPrices[currency] = currentPrice;
          // Include price ID only if this is an update (not a new price)
          if (originalPrice !== undefined && priceIds[currency]) {
            changedPriceIds[currency] = priceIds[currency];
          }
        }
      });
    } else {
      // For new plans, include all prices
      Object.assign(changedPrices, formData.prices);
    }
    
    console.log('OneTimePlanEditor - Original prices:', originalPrices);
    console.log('OneTimePlanEditor - Current prices:', formData.prices);
    console.log('OneTimePlanEditor - Changed prices:', changedPrices);
    console.log('OneTimePlanEditor - Changed price IDs:', changedPriceIds);
    
    // Prepare data for submission
    const submitData = {
      ...formData,
      // Send features as a simple array (like CreditPackageEditor does)
      features: features.length > 0 ? features : undefined,
      // Structure pricing data with selective updates
      // Only include prices that have actually changed
      pricing: {
        prices: changedPrices,
        priceIds: changedPriceIds
      },
      // Add translations in the new format
      translations: {
        name: Object.keys(nameTranslations).length > 0 ? nameTranslations : undefined,
        description: Object.keys(descriptionTranslations).length > 0 ? descriptionTranslations : undefined,
        // Convert internal indexedFeatures to MultiLanguageStringArray
        features: Object.keys(featuresTranslations).length > 0 ?
          Object.keys(featuresTranslations).reduce((acc: { [locale: string]: string[] }, locale) => {
            acc[locale] = featuresTranslations[locale];
            return acc;
          }, {}) : undefined
      },
      isRecommended,
      offerFreeTrial,
      freeTrialDays: offerFreeTrial ? freeTrialDays : undefined,
      featureFlags: selectedFeatureFlags
    };
    
    console.log('OneTimePlanEditor - Submit Data:', submitData);

    try {
      if (isEditing && plan?.id) {
        console.log('OneTimePlanEditor - Updating plan:', plan.id);
        await updateMutation.mutateAsync({
          productId,
          planId: plan.id,
          data: submitData
        });
      } else {
        console.log('OneTimePlanEditor - Creating new plan');
        await createMutation.mutateAsync({
          productId,
          data: submitData
        });
      }
      
      console.log('OneTimePlanEditor - Save successful, closing dialog');
      onClose();
    } catch (error) {
      console.error('OneTimePlanEditor - Save error:', error);
      // The mutation hooks should already show toast errors, but let's be explicit
      toast.error(t('products.oneTimePlan.toastSaveError'));
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] text-white border-zinc-800">
        <DialogHeader>
          <div className="flex items-center justify-between mb-2">
            <DialogTitle className="text-lg font-normal">
              {t('products.oneTimePlan.dialogTitle')}
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-6 w-6 rounded-sm hover:bg-zinc-800"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-zinc-400">
            {t('products.oneTimePlan.dialogDescription')}
          </p>
        </DialogHeader>

        <div className="space-y-6 pt-6">
          {/* Plan Name and Description - Side by Side */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="name" className="text-sm font-normal text-white">
                  {t('products.oneTimePlan.nameLabel')}
                </Label>
                <TranslationButton
                  onClick={() => setShowTranslationDialog(true)}
                  translationStatus={
                    Object.keys(nameTranslations).length > 0 ? 'complete' : 'none'
                  }
                />
              </div>
              <Input
                id="name"
                placeholder={t('products.oneTimePlan.namePlaceholder')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-transparent border-zinc-700 text-white placeholder:text-zinc-600 h-9"
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="description" className="text-sm font-normal text-white">
                  {t('products.oneTimePlan.descriptionLabel')}
                </Label>
                <TranslationButton
                  onClick={() => setShowDescriptionTranslationDialog(true)}
                  translationStatus={
                    Object.keys(descriptionTranslations).length > 0 ? 'complete' : 'none'
                  }
                />
              </div>
              <Textarea
                id="description"
                placeholder={t('products.oneTimePlan.descriptionPlaceholder')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-transparent border-zinc-700 text-white placeholder:text-zinc-600 h-9 min-h-[38px]"
                rows={1}
              />
            </div>
          </div>

          {/* One-Time Payment Price */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-normal text-white">{t('products.oneTimePlan.priceSectionTitle')}</h3>
              <p className="text-xs text-zinc-500 mt-0.5">{t('products.oneTimePlan.priceSectionDescription')}</p>
            </div>
            
            <div className="space-y-4">
              {activeCurrencies.map((currency) => {
                const price = formData.prices?.[currency] || 0;
                const hasError = validationErrors[`price-${currency}`];
                
                return (
                  <div key={currency} className="flex items-center gap-2">
                    <div className="flex items-center gap-2 min-w-[80px]">
                      <Label className="text-sm text-white">{currency}</Label>
                      {currency === defaultCurrency && (
                        <span className="px-2 py-0.5 bg-zinc-800 text-xs rounded text-zinc-400">
                          {t('products.defaultBadgeLabel')}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-zinc-400">
                          {currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : currency}
                        </span>
                        <Input
                          type="number"
                          step="0.01"
                          value={price}
                          onChange={(e) => {
                            const newPrice = parseFloat(e.target.value) || 0;
                            handlePricesChange({
                              ...formData.prices,
                              [currency]: newPrice
                            });
                          }}
                          placeholder={t('products.oneTimePlan.pricePlaceholder')}
                          className={`bg-transparent text-white border-zinc-700 flex-1 ${hasError ? 'border-red-500' : ''}`}
                        />
                      </div>
                      {hasError && (
                        <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {hasError}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Access Duration */}
          <div className="space-y-4">
            <h3 className="text-sm font-normal text-white">{t('products.oneTimePlan.accessDurationTitle')}</h3>
            
            <div className="flex items-center gap-4">
              {!isUnlimited && (
                <div className="flex items-center gap-2">
                  <Input
                    id="duration"
                    type="number"
                    min="1"
                    max="120"
                    value={customDuration}
                    onChange={(e) => handleDurationChange(e.target.value)}
                    placeholder="12"
                    className="bg-transparent border-zinc-700 text-white h-9 w-24"
                  />
                  <span className="text-sm text-zinc-400">{t('products.oneTimePlan.monthsLabel')}</span>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="unlimited"
                  checked={isUnlimited}
                  onCheckedChange={(checked) => handleUnlimitedChange(checked === true)}
                  className="border-zinc-600 data-[state=checked]:bg-primary data-[state=checked]:border-primary h-4 w-4"
                />
                <Label htmlFor="unlimited" className="text-sm text-white font-normal cursor-pointer">
                  {t('products.oneTimePlan.unlimitedAccessLabel')}
                </Label>
              </div>
            </div>
          </div>


          {/* Features */}
          <div className="space-y-2">
            <div>
              <Label className="text-sm font-normal text-white">{t('products.oneTimePlan.featuresTitle')}</Label>
              <p className="text-xs text-zinc-500 mt-0.5">{t('products.oneTimePlan.featuresDescription')}</p>
            </div>
            
            <div className="flex items-center gap-2">
              <Input
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                placeholder={t('products.oneTimePlan.featurePlaceholder')}
                className="bg-transparent border-zinc-700 text-white placeholder:text-zinc-600 h-9 flex-1"
              />
              <Button
                onClick={handleAddFeature}
                variant="outline"
                size="sm"
                className="border-zinc-700 hover:bg-zinc-800 text-white h-9 px-3"
              >
                <Plus className="h-3 w-3 mr-1" />
                {t('common.add')}
              </Button>
              {features.length > 0 && (
                <TranslationButton
                  onClick={() => setShowFeaturesTranslationDialog(true)}
                  translationStatus={
                    Object.keys(featuresTranslations).length > 0 ? 'complete' : 'none'
                  }
                />
              )}
            </div>
            
            {features.length > 0 && (
              <div className="space-y-2 mt-2">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center justify-between p-2 border border-zinc-700 rounded bg-zinc-900/30">
                    <span className="text-sm text-white">{feature}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFeature(index)}
                      className="text-zinc-400 hover:text-white transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Feature Flags */}
          {availableFeatureFlags && availableFeatureFlags.length > 0 && (
            <div className="space-y-2">
              <div>
                <Label className="text-sm font-normal text-white">{t('products.oneTimePlan.featureFlagsTitle')}</Label>
                <p className="text-xs text-zinc-500 mt-0.5">{t('products.oneTimePlan.featureFlagsDescription')}</p>
              </div>
              
              <div className="space-y-2.5 border border-zinc-700 rounded-md p-4 bg-zinc-900/30">
                {availableFeatureFlags.map((flag) => (
                  <div key={flag.key} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={flag.key}
                        checked={selectedFeatureFlags[flag.key] || false}
                        onCheckedChange={() => handleFeatureFlagToggle(flag.key)}
                        className="border-zinc-600 data-[state=checked]:bg-primary data-[state=checked]:border-primary h-4 w-4"
                      />
                      <Label 
                        htmlFor={flag.key}
                        className="text-sm text-white font-normal cursor-pointer"
                      >
                        {flag.name || flag.key}
                      </Label>
                    </div>
                    <span className="text-xs text-zinc-500 font-mono">{flag.key}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Additional Settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-normal text-white">{t('products.oneTimePlan.additionalSettingsTitle')}</h3>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  id="recommended"
                  checked={isRecommended}
                  onCheckedChange={setIsRecommended}
                  className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-zinc-700"
                />
                <Label htmlFor="recommended" className="text-sm text-white font-normal cursor-pointer">
                  {t('products.oneTimePlan.recommendedLabel')}
                </Label>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Switch
                    id="free-trial"
                    checked={offerFreeTrial}
                    onCheckedChange={setOfferFreeTrial}
                    className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-zinc-700"
                  />
                  <Label htmlFor="free-trial" className="text-sm text-white font-normal cursor-pointer">
                    {t('products.oneTimePlan.freeTrialLabel')}
                  </Label>
                </div>
                
                {offerFreeTrial && (
                  <div className="ml-12 space-y-2">
                    <Label htmlFor="trial-days" className="text-xs text-zinc-400 font-normal">
                      {t('products.oneTimePlan.freeTrialDaysLabel')}
                    </Label>
                    <Input
                      id="trial-days"
                      type="number"
                      min="1"
                      max="90"
                      value={freeTrialDays}
                      onChange={(e) => setFreeTrialDays(parseInt(e.target.value) || 14)}
                      className="bg-transparent border-zinc-700 text-white h-9 w-32"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6 flex justify-end gap-3">
          <Button 
            variant="outline" 
            onClick={onClose} 
            disabled={isLoading}
            className="border-zinc-600 bg-transparent hover:bg-zinc-800 text-white h-9 px-4 font-normal"
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isLoading}
            className="bg-white text-black hover:bg-zinc-100 h-9 px-4 font-normal"
          >
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('products.oneTimePlan.saveButton')}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Translation Dialogs */}
      <TranslationDialog
        open={showTranslationDialog}
        onOpenChange={setShowTranslationDialog}
        value={formData.name}
        translations={nameTranslations}
        onChange={setNameTranslations}
        fieldType="input"
        label={t('products.oneTimePlan.translationNameLabel')}
        supportedLanguages={supportedLanguages}
      />

      <TranslationDialog
        open={showDescriptionTranslationDialog}
        onOpenChange={setShowDescriptionTranslationDialog}
        value={formData.description || ''}
        translations={descriptionTranslations}
        onChange={setDescriptionTranslations}
        fieldType="textarea"
        label={t('products.oneTimePlan.translationDescriptionLabel')}
        supportedLanguages={supportedLanguages}
      />

      <FeaturesTranslationDialog
        open={showFeaturesTranslationDialog}
        onOpenChange={setShowFeaturesTranslationDialog}
        features={features}
        translations={featuresTranslations}
        onChange={setFeaturesTranslations}
        supportedLanguages={supportedLanguages}
      />
    </Dialog>
  );
}
